import { useState } from 'react';
import { api } from '../api/client.js';
import ConfirmDialog from '../components/ConfirmDialog.jsx';

/**
 * Súper administrador: alta de una empresa nueva por plantilla maestra.
 * Flujo:
 *  1) Descargar plantilla.
 *  2) Subir el archivo en simulación (dry-run) y revisar el resumen.
 *  3) Confirmar y aplicar.
 */
export default function SuperadminOnboarding() {
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(null);
    const [error, setError] = useState(null);
    const [appliedAt, setAppliedAt] = useState(null);
    const [applyConfirmOpen, setApplyConfirmOpen] = useState(false);

    function reset() {
        setFile(null);
        setPreview(null);
        setError(null);
        setAppliedAt(null);
    }

    async function handleDownload() {
        setBusy('download');
        setError(null);
        try {
            await api.downloadOnboardingTemplate();
        } catch (err) {
            setError(
                err.message ||
                    'No se pudo descargar la plantilla. Compruebe que la API esté en marcha (puerto 3001) y que inició sesión como súper admin (ADM001).'
            );
        } finally {
            setBusy(null);
        }
    }

    async function handlePreview(selected) {
        if (!selected) return;
        setFile(selected);
        setPreview(null);
        setError(null);
        setAppliedAt(null);
        setBusy('preview');
        try {
            const result = await api.uploadOnboardingFile(selected, { dryRun: true });
            setPreview(result);
        } catch (err) {
            setError(err.message || 'No se pudo simular la carga');
        } finally {
            setBusy(null);
        }
    }

    function requestApply() {
        if (!file) return;
        setApplyConfirmOpen(true);
    }

    async function handleApplyConfirmed() {
        if (!file) return;
        setBusy('apply');
        setError(null);
        try {
            const result = await api.uploadOnboardingFile(file, { dryRun: false });
            setPreview(result);
            setAppliedAt(new Date().toISOString());
            setApplyConfirmOpen(false);
        } catch (err) {
            setError(err.message || 'No se pudo aplicar la carga');
        } finally {
            setBusy(null);
        }
    }

    const errors = preview?.errors ?? [];
    const summary = preview?.summary;

    return (
        <div className="container animate-in" style={{ maxWidth: '960px' }}>
            <header className="mb-xl">
                <h2>🏗️ Implementación de Empresa (Súper Admin)</h2>
                <p className="text-muted">
                    Plantilla maestra Excel para activar un cliente nuevo: empresa, áreas, empleados,
                    maestros CID, causales y mensajes. Toda la carga es transaccional y idempotente.
                </p>
            </header>

            <section className="glass-card mb-lg">
                <h3>1. Descargar plantilla</h3>
                <p className="text-sm text-muted mb-md">
                    Se descarga el archivo <code>ALINEA_Plantilla_Implementacion.xlsx</code> con
                    instrucciones y un ejemplo por hoja. Diligéncielo con datos reales del cliente.
                </p>
                <button
                    type="button"
                    className="btn btn-primary"
                    disabled={busy === 'download'}
                    onClick={handleDownload}
                >
                    {busy === 'download' ? 'Generando…' : 'Descargar plantilla'}
                </button>
            </section>

            <section className="glass-card mb-lg">
                <h3>2. Subir y previsualizar</h3>
                <p className="text-sm text-muted mb-md">
                    El archivo se valida en una simulación (no escribe en base) y se muestra el resumen.
                    Si hay errores, corrija el Excel y vuelva a subir.
                </p>
                <input
                    type="file"
                    accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    disabled={busy === 'preview' || busy === 'apply'}
                    onChange={(e) => handlePreview(e.target.files?.[0])}
                />
                {file ? (
                    <p className="text-xs text-muted mt-sm">
                        Archivo seleccionado: <strong>{file.name}</strong>
                    </p>
                ) : null}
                {busy === 'preview' ? <p className="text-sm mt-sm">Simulando carga…</p> : null}
            </section>

            {error ? (
                <div
                    className="glass-card mb-lg"
                    style={{ borderLeft: '4px solid #dc2626', background: 'rgba(220,38,38,0.06)' }}
                >
                    <strong>Error:</strong> {error}
                </div>
            ) : null}

            {errors.length > 0 ? (
                <section
                    className="glass-card mb-lg"
                    style={{ borderLeft: '4px solid #f59e0b', background: 'rgba(245,158,11,0.08)' }}
                >
                    <h3>Errores de validación ({errors.length})</h3>
                    <ul className="text-sm" style={{ paddingLeft: '1.2rem' }}>
                        {errors.map((e, idx) => (
                            <li key={idx}>
                                <strong>{e.sheet}</strong>
                                {e.row > 0 ? ` (fila ${e.row})` : ''}: {e.message}
                            </li>
                        ))}
                    </ul>
                </section>
            ) : null}

            {summary ? (
                <section className="glass-card mb-lg">
                    <h3>3. Resumen {preview?.dryRun ? '(simulación)' : '(aplicado)'}</h3>
                    <div className="text-sm" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: '0.75rem' }}>
                        <SummaryCard
                            title="Empresa"
                            primary={summary.tenant?.name || '—'}
                            secondary={`slug: ${summary.tenant?.slug || '—'} · ${summary.tenant?.action || '—'}`}
                        />
                        <SummaryCard
                            title="Áreas"
                            primary={`${summary.areas?.total ?? 0} total`}
                            secondary={`creadas ${summary.areas?.created ?? 0} · actualizadas ${summary.areas?.updated ?? 0}`}
                        />
                        <SummaryCard
                            title="Empleados"
                            primary={`${summary.employees?.total ?? 0} total`}
                            secondary={`creados ${summary.employees?.created ?? 0} · actualizados ${summary.employees?.updated ?? 0}`}
                        />
                        <SummaryCard
                            title="Maestros CID"
                            primary={`${summary.rotation_costs?.upserted ?? 0} upserts`}
                            secondary={`total tenant: ${summary.rotation_costs?.total ?? 0}`}
                        />
                        <SummaryCard
                            title="Causales retiro"
                            primary={`+${summary.retirement_reasons?.created ?? 0}`}
                            secondary={`total: ${summary.retirement_reasons?.total ?? 0}`}
                        />
                        <SummaryCard
                            title="Mensajes"
                            primary={`+${summary.messages?.created ?? 0}`}
                            secondary={`omitidos ${summary.messages?.skipped ?? 0} · total ${summary.messages?.total ?? 0}`}
                        />
                    </div>

                    {(summary.employees?.dateSamples?.length ?? 0) > 0 ? (
                        <div className="mt-md">
                            <h4 className="text-sm" style={{ margin: '0 0 0.5rem' }}>
                                Fechas detectadas (primeras filas)
                            </h4>
                            <div style={{ overflowX: 'auto' }}>
                                <table className="text-sm" style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr>
                                            <th>Código</th>
                                            <th>Nacimiento</th>
                                            <th>Ingreso</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {summary.employees.dateSamples.map((row) => (
                                            <tr key={row.employee_code}>
                                                <td>
                                                    <code>{row.employee_code}</code>
                                                </td>
                                                <td>
                                                    {row.birth_date || (
                                                        <span className="text-muted">
                                                            {row.birth_error || '—'}
                                                        </span>
                                                    )}
                                                </td>
                                                <td>
                                                    {row.hire_date || (
                                                        <span className="text-muted">
                                                            {row.hire_error || '—'}
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <p className="text-xs text-muted mt-sm">
                                Si aparece «—» o un error, corrija el Excel (formato AAAA-MM-DD o celda tipo Fecha) antes
                                de aplicar.
                            </p>
                        </div>
                    ) : null}

                    {preview?.dryRun && errors.length === 0 ? (
                        <div className="mt-lg flex gap-sm" style={{ flexWrap: 'wrap' }}>
                            <button
                                type="button"
                                className="btn btn-primary"
                                disabled={busy === 'apply'}
                                onClick={requestApply}
                            >
                                {busy === 'apply' ? 'Aplicando…' : 'Confirmar y aplicar'}
                            </button>
                            <button type="button" className="btn btn-secondary" onClick={reset}>
                                Descartar y reintentar
                            </button>
                        </div>
                    ) : null}

                    {appliedAt ? (
                        <p className="text-sm mt-md" style={{ color: 'var(--color-alert-green, #16a34a)' }}>
                            ✅ Carga aplicada {new Date(appliedAt).toLocaleString()}.
                        </p>
                    ) : null}
                </section>
            ) : null}

            <section className="glass-card">
                <h3>Notas</h3>
                <ul className="text-sm" style={{ paddingLeft: '1.2rem' }}>
                    <li>La hoja EMPRESA es obligatoria (nombre + slug).</li>
                    <li>Si el slug ya existe, no se crea otra empresa: se actualiza el branding y se cargan los maestros sobre la existente.</li>
                    <li>Los empleados con rol <code>admin</code> o <code>supervisor</code> requieren contraseña (mín. 6).</li>
                    <li>Los porcentajes (brecha) van entre 0 y 100; se almacenan internamente como 0–1.</li>
                    <li>El upload es solo para súper administradores; se mantiene el resto de los flujos existentes intactos.</li>
                </ul>
            </section>

            <ConfirmDialog
                open={applyConfirmOpen}
                title="Aplicar implementación"
                message="Esto creará o actualizará datos reales del cliente en la plataforma."
                detail="Revise el resumen de la simulación. Si el slug de empresa ya existe, se actualizará el tenant existente (carga idempotente)."
                confirmLabel="Sí, aplicar"
                cancelLabel="Cancelar"
                tone="warning"
                busy={busy === 'apply'}
                onConfirm={() => void handleApplyConfirmed()}
                onCancel={() => {
                    if (busy !== 'apply') setApplyConfirmOpen(false);
                }}
            />
        </div>
    );
}

function SummaryCard({ title, primary, secondary }) {
    return (
        <div
            style={{
                background: 'rgba(0,0,0,0.03)',
                borderRadius: 8,
                padding: '0.75rem 0.9rem',
                lineHeight: 1.35,
            }}
        >
            <div className="text-xs text-muted" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {title}
            </div>
            <div style={{ fontWeight: 700, fontSize: '1.05rem', marginTop: '0.15rem' }}>{primary}</div>
            <div className="text-xs text-muted">{secondary}</div>
        </div>
    );
}
